import { SelectInput } from '@/components/form';
import { PermissionCategories } from '@/features/settings/constants/permissions';
import {
  IAppRole,
  IUser
} from "@/types";
import { Permission, PermissionState } from '@/types/permissions.types';
import { Alert, Card, Checkbox, Col, Collapse, Form, Modal, Row, Space } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppRoleList, useAssignRole, useCreateAppRole, useUnassignRole, useUpdateAppRole, useUpdateUser } from '../../hooks';

const { Panel } = Collapse;

// Helper functions to extract roles from user data
const getDefaultRole = (user: IUser | null): IAppRole | null => {
  if (!user?.dogma_AppRole_SystemUser_SystemUser || user.dogma_AppRole_SystemUser_SystemUser.length === 0) {
    return null;
  }
  // Find the default role (where dogma_defaultsecurityrole is true)
  return user.dogma_AppRole_SystemUser_SystemUser.find(
    (role) => role.dogma_defaultsecurityrole === true
  ) || null;
};

const getCustomRole = (user: IUser | null): IAppRole | null => {
  if (!user?.dogma_AppRole_SystemUser_SystemUser || user.dogma_AppRole_SystemUser_SystemUser.length === 0) {
    return null;
  }
  // Find the custom role (where dogma_defaultsecurityrole is false)
  return user.dogma_AppRole_SystemUser_SystemUser.find(
    (role) => role.dogma_defaultsecurityrole === false
  ) || null;
};

interface EditRoleModalProps {
  open: boolean;
  user: IUser | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditRoleModal: React.FC<EditRoleModalProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [customPermissions, setCustomPermissions] = useState<Permission[]>([]);
  const updateUserMutation = useUpdateUser();
  const assignRoleMutation = useAssignRole();
  const createAppRoleMutation = useCreateAppRole();
  const customRoleUpdate = useUpdateAppRole();
  const deleteAssignRole = useUnassignRole();

  // Fetch roles from API
  const { data: rolesData, isLoading: isLoadingRoles } = useAppRoleList({
    queryParams: { current: 0, pageSize: 100 },
    enabled: open,
  });

  // Get current user roles from user data
  const currentDefaultRole = useMemo(() => getDefaultRole(user), [user]);
  const currentCustomRole = useMemo(() => getCustomRole(user), [user]);

  // Watch selected role
  const selectedRoleId = Form.useWatch('role', form);
  // Get base permissions from selected role
  const basePermissions = useMemo(() => {
    if (!selectedRoleId || !rolesData?.value) return [];

    const selectedRoleData = rolesData.value.find(
      (role) => role.dogma_approleid === selectedRoleId
    );

    if (!selectedRoleData || !selectedRoleData.dogma_permissionsjson) return [];

    try {
      const permissionsJson: PermissionState = JSON.parse(
        selectedRoleData.dogma_permissionsjson
      );

      const enabledPermissions: Permission[] = [];

      Object.entries(permissionsJson).forEach(([permissionId, permValue]: [string, any]) => {
        // Handle both old format {enabled, modified} and new format (boolean)
        const isEnabled = typeof permValue === 'boolean' ? permValue : permValue?.enabled;

        if (isEnabled === true) {
          enabledPermissions.push(permissionId as Permission);
        }
      });

      return enabledPermissions;
    } catch (error) {
      return [];
    }
  }, [selectedRoleId, rolesData]);

  const roleOptions = rolesData?.value?.map((role) => ({
    label: role.dogma_name,
    value: role.dogma_approleid,
  })) || [];

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        role: currentDefaultRole?.dogma_approleid || '',
      });

      // Populate custom permissions if they exist
      if (currentCustomRole?.dogma_permissionsjson && currentDefaultRole?.dogma_permissionsjson) {
        try {
          const customRoleJson: PermissionState = JSON.parse(currentCustomRole.dogma_permissionsjson);
          const defaultRoleJson: PermissionState = JSON.parse(currentDefaultRole.dogma_permissionsjson);


          // Extract custom permissions (ones in custom role but not in default role)
          const customPermissionsList: Permission[] = [];

          Object.entries(customRoleJson).forEach(([permId, permValue]: [string, any]) => {
            // Check if permission is enabled in custom role
            const isEnabled = typeof permValue === 'boolean' ? permValue : permValue?.enabled;

            // Check if this permission exists in the default role
            const isInDefaultRole = defaultRoleJson.hasOwnProperty(permId);

            // If enabled and NOT in default role, it's a custom permission
            if (isEnabled && !isInDefaultRole) {
              customPermissionsList.push(permId as Permission);
            }
          });

          setCustomPermissions(customPermissionsList);
        } catch (error) {
          setCustomPermissions([]);
        }
      } else {
        setCustomPermissions([]);
      }
    } else {
      setCustomPermissions([]);
    }
  }, [open, user, form, currentDefaultRole, currentCustomRole]);
  // Add default role to user (when no role exists)
  const handleDefaultRoleAdd = async (roleId: string) => {
    await assignRoleMutation.mutateAsync({
      systemuserid: user!.systemuserid,
      roleid: roleId,
    });
  };

  // Update default role for user (when role already exists)
  const handleDefaultRoleUpdate = async (newRoleId: string) => {
    if (!currentDefaultRole) return;
    // Assign the new role (this will replace the old role assignment)
    await deleteAssignRole.mutateAsync({
      id: currentDefaultRole.dogma_approleid,
      systemUserId: user!.systemuserid,
      appRoleId: currentDefaultRole.dogma_approleid,
    });
    await handleDefaultRoleAdd(newRoleId);
  };

  // Create and assign a new custom role
  const handleCustomRoleAdd = async () => {
    // Build permissions JSON for custom role
    const rolePermissions: {
      [permission: string]: boolean;
    } = {};

    // Get all permissions from base role
    basePermissions.forEach((perm) => {
      rolePermissions[perm] = true;
    });

    // Add custom permissions
    customPermissions.forEach((perm) => {
      rolePermissions[perm] = true;
    });

    const permissionState: PermissionState = {
      ...rolePermissions,
    };

    const customRoleName = `${user!.firstname} ${user!.lastname} - Custom Role`;
    const createCustomRolePayload = {
      dogma_defaultsecurityrole: false,
      dogma_name: customRoleName,
      dogma_permissionsjson: JSON.stringify(permissionState),
    };

    const createdCustomRole = await createAppRoleMutation.mutateAsync(createCustomRolePayload);

    // Assign the custom role to the user
    await assignRoleMutation.mutateAsync({
      systemuserid: user!.systemuserid,
      roleid: createdCustomRole.dogma_approleid,
    });
  };

  // Update existing custom role (creates new one and reassigns)
  const handleCustomRoleUpdate = async (customRoleId: string) => {
    // Build updated permissions JSON
    const rolePermissions: {
      [permission: string]: boolean;
    } = {};

    basePermissions.forEach((perm) => {
      rolePermissions[perm] = true;
    });

    customPermissions.forEach((perm) => {
      rolePermissions[perm] = true;
    });

    const permissionState: PermissionState = {
      ...rolePermissions,
    };

    const customRoleName = `${user!.firstname} ${user!.lastname} - Custom Role`;

    // Create a new custom role with updated permissions
    const createCustomRolePayload = {
      dogma_defaultsecurityrole: false,
      dogma_name: customRoleName,
      dogma_permissionsjson: JSON.stringify(permissionState),
    };

    await customRoleUpdate.mutateAsync({
      id: customRoleId,
      payload: {
        ...createCustomRolePayload,
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Handle default role assignment/update
      if (values.role) {
        const currentRoleId = currentDefaultRole?.dogma_approleid;

        // Check if role changed
        if (values.role !== currentRoleId) {
          if (currentRoleId) {
            // Role exists, so update it
            await handleDefaultRoleUpdate(values.role);
          } else {
            // No existing role, so add it
            await handleDefaultRoleAdd(values.role);
          }
        }
      }

      // Handle custom permissions
      if (customPermissions.length > 0) {
        const existingCustomRoleId = currentCustomRole?.dogma_approleid;

        if (existingCustomRoleId) {
          await handleCustomRoleUpdate(existingCustomRoleId);
        } else {
          // No custom role exists, create new one
          await handleCustomRoleAdd();
        }
      }

      onSuccess();
      onClose();
    } catch (error) {
    }
  };

  return (
    <Modal
      title="Edit Role & Permissions"
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Save"
      cancelText="Cancel"
      centered
      width={700}
      confirmLoading={updateUserMutation.isPending || assignRoleMutation.isPending || createAppRoleMutation.isPending || deleteAssignRole.isPending}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '24px'
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col xs={24}>
            <SelectInput
              name="role"
              label="User Role"
              placeholder={isLoadingRoles ? "Loading roles..." : "Select role"}
              options={roleOptions}
              loading={isLoadingRoles}
              disabled={isLoadingRoles}
              required
            />
          </Col>
        </Row>

        {/* Custom Permissions Section */}
        {selectedRoleId && (() => {
          // Calculate total available permissions across all categories
          const totalAvailablePermissions = PermissionCategories.reduce((count, category) => {
            const availableInCategory = category.permissions.filter(
              (perm) => !basePermissions.includes(perm.id)
            );
            return count + availableInCategory.length;
          }, 0);

          return (
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Custom Permissions</h4>
              <p className="text-sm text-gray-600 mb-3">
                Grant additional permissions beyond the base role
              </p>

              {totalAvailablePermissions === 0 ? (
                <Alert
                  message="All Permissions Included"
                  description="This role already includes all available permissions in the system. No additional custom permissions can be granted."
                  type="info"
                  showIcon
                />
              ) : (
                <Collapse accordion>
                  {PermissionCategories.map((category) => {
                    // Filter out permissions that are already in base role
                    const availablePermissions = category.permissions.filter(
                      (perm) => !basePermissions.includes(perm.id)
                    );

                    // Skip category if all permissions are base permissions
                    if (availablePermissions.length === 0) {
                      return null;
                    }

                    return (
                      <Panel header={category.name} key={category.id}>
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          {availablePermissions.map((perm) => {
                            const isCustom = customPermissions.includes(perm.id);

                            return (
                              <Card
                                key={perm.id}
                                size="small"
                                className={isCustom ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}
                              >
                                <Space align="start" style={{ width: '100%' }}>
                                  <Checkbox
                                    checked={isCustom}
                                    onChange={() => {
                                      setCustomPermissions((prev) => {
                                        if (prev.includes(perm.id)) {
                                          return prev.filter((p) => p !== perm.id);
                                        }
                                        return [...prev, perm.id];
                                      });
                                    }}
                                  />
                                  <div style={{ flex: 1 }}>
                                    <div className="font-medium text-gray-900">{perm.label}</div>
                                    <div className="text-gray-600 text-xs mt-1">{perm.description}</div>
                                  </div>
                                </Space>
                              </Card>
                            );
                          })}
                        </Space>
                      </Panel>
                    );
                  })}
                </Collapse>
              )}
            </div>
          );
        })()}
      </Form>
    </Modal>
  );
};

export default EditRoleModal;
